import { createContext, useContext, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "../i18n";

const LOCALE_STORAGE_KEY = "repradar-locale";

function isSpanishPreferred() {
  const lang = navigator.language || navigator.userLanguage || "";
  const langs = navigator.languages || [lang];
  return langs.some((l) => (l || "").toLowerCase().startsWith("es"));
}

function getStoredLocale() {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setStoredLocale(locale) {
  try {
    if (locale) {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } else {
      localStorage.removeItem(LOCALE_STORAGE_KEY);
    }
  } catch {}
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isEsPath = pathname.startsWith("/es");
  const pathWithoutLocale = isEsPath ? pathname.slice(3) || "/" : pathname;

  const locale = isEsPath ? "es" : "en";
  const basePath = isEsPath ? "/es" : "";

  const switchLocale = useCallback(
    (newLocale) => {
      setStoredLocale(newLocale);
      i18n.changeLanguage(newLocale);

      if (newLocale === "es") {
        const newPath = pathWithoutLocale === "/" ? "/es" : `/es${pathWithoutLocale}`;
        navigate(newPath);
      } else {
        navigate(pathWithoutLocale || "/");
      }
    },
    [navigate, pathWithoutLocale]
  );

  const localizePath = useCallback(
    (path) => {
      if (locale === "es") {
        const clean = path.startsWith("/") ? path : `/${path}`;
        return clean === "/" ? "/es" : `/es${clean}`;
      }
      return path.startsWith("/") ? path : `/${path}`;
    },
    [locale]
  );

  useEffect(() => {
    i18n.changeLanguage(locale);
    if (isEsPath) setStoredLocale("es");
  }, [locale, isEsPath]);

  const value = {
    locale,
    basePath,
    localizePath,
    switchLocale,
    pathWithoutLocale,
  };

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function LocaleGate({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  useEffect(() => {
    if (pathname.startsWith("/es")) return;

    const stored = getStoredLocale();
    if (stored === "es") {
      const newPath = pathname === "/" ? "/es" : `/es${pathname}`;
      navigate(newPath, { replace: true });
      return;
    }
    if (stored === "en") return;

    if (isSpanishPreferred()) {
      const newPath = pathname === "/" ? "/es" : `/es${pathname}`;
      setStoredLocale("es");
      navigate(newPath, { replace: true });
    }
  }, [pathname, navigate]);

  return children;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return ctx;
}
